import { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Typography, IconButton } from '@material-ui/core';
import { useQuery, useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import _debounce from 'lodash/debounce';
import DeleteIcon from '@material-ui/icons/Delete';
import _capitalize from 'lodash/capitalize';
import { GET_COLLECTION_TYPE_PROPERTY_LIST_ITEMS } from '../../graphql/query/collectionTypePropertyListItems';
import { DELETE_COLLECTION_TYPE_PROPERTY_ITEM } from '../../graphql/mutation/collectionTypePropertyListItems';

import DashboardTable from '../../components/dashboard-table';
import DashboardFilters from '../../components/dashboard-filters';
import ReplaceCollectionTypePropertyItemButton from '../../components/replace-collection-type-property-item-button';
import EditCollectionTypePropertyItemButton from '../../components/edit-collection-type-property-item-button';
import PropertyDashboardThumbnail from './property-dashboard-thumbnail';
import PropertyDashboardLink from './property-dashboard-link';
import { GET_COLLECTION_TYPE_PROPERTIES } from '../../graphql/query/collectionTypeProperties';
import PropertyItemsLink from './property-items-link';
import notifyDeleteItemError from './notify-delete-item-error';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    maxHeight: `calc(100vh - ${theme.spacing(8)}px)`,
    height: `calc(100vh - ${theme.spacing(8)}px)`,
    display: 'grid',
    gridTemplateRows: `${theme.spacing(6)}px ${theme.spacing(4.5)}px 1fr`,
    gridGap: theme.spacing(2),
    boxSizing: 'border-box',
  },
  tabs: {},
  title: {
    lineHeight: `${theme.spacing(6)}px`,
  },
  filters: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttons: {
    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
  addLink: {
    textDecoration: 'none',
  },
  table: {
    overflow: 'hidden',
  },
  unpublished: {
    color: theme.palette.error.main,
  },
  published: {
    color: theme.palette.success.main,
  },
}));

const PropertyDashboard = () => {
  const classes = useStyles();
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [property, setProperty] = useState({});

  const variables = {
    limit: rowsPerPage,
    filter: {
      term,
      published: false,
    },
    propertySlug: property.value || undefined,
  };

  const { data: propertiesData } = useQuery(GET_COLLECTION_TYPE_PROPERTIES, {});

  const propertiesOptions =
    propertiesData?.collectionTypeProperties?.results.map((prop) => ({
      label: `${prop.name} (${prop.collectionTypes?.map((ct) => ct.name).join(', ')})`,
      value: prop.slug,
    })) || [];

  const {
    data: itemsResponseData,
    loading,
    refetch: refetchItems,
  } = useQuery(GET_COLLECTION_TYPE_PROPERTY_LIST_ITEMS, { variables });

  const itemsData = itemsResponseData?.collectionTypePropertyListItems;
  const items = itemsData?.results;

  const { enqueueSnackbar } = useSnackbar();

  const [deleteCollectionTypePropertyItem] = useMutation(DELETE_COLLECTION_TYPE_PROPERTY_ITEM, {
    onCompleted: updateItems,
    onError: (error) => notifyDeleteItemError(error, enqueueSnackbar),
  });

  const debounceUpdateItems = useCallback(_debounce(updateItems, 500), []);

  useEffect(() => {
    const filter = { term, published: false };

    debounceUpdateItems({
      ...variables,
      limit: rowsPerPage,
      filter,
      propertySlug: property.value || undefined,
    });
  }, [term, property]);

  useEffect(() => {
    updateItems();
  }, [rowsPerPage]);

  const COLUMNS = [
    {
      id: 'published',
      label: 'Published',
      minWidth: 130,
      width: 130,
      format: renderPublished,
    },
    {
      id: 'media',
      label: 'Media',
      minWidth: 50,
      width: 50,
      format: (prop) => <PropertyDashboardThumbnail property={prop} />,
    },
    {
      id: 'id',
      label: 'ID',
      minWidth: 60,
      width: 60,
      format: ({ id }) => id,
    },
    {
      id: 'name',
      label: 'Name',
      minWidth: 100,
    },
    {
      id: 'type',
      label: 'Type',
      minWidth: 200,
      format: (prop) => `${prop?.collectionTypeProperty?.name}` || 'No type',
    },
    {
      id: 'items',
      label: 'Items',
      minWidth: 40,
      width: 40,
      format: (listItem) => <PropertyItemsLink listItem={listItem} />,
    },
    {
      id: 'replace',
      minWidth: 40,
      width: 40,
      format: (listItem) => (
        <ReplaceCollectionTypePropertyItemButton
          updateItems={updateItems}
          listItem={listItem}
          variant="icon"
          slug={listItem?.collectionTypeProperty?.slug}
        />
      ),
    },
    {
      id: 'edit',
      minWidth: 40,
      width: 40,
      format: (prop) => (
        <EditCollectionTypePropertyItemButton
          property={prop}
          updateItems={updateItems}
          slug={prop?.collectionTypeProperty?.slug}
        />
      ),
    },
    { id: 'delete', minWidth: 40, width: 40, format: renderDelete },
  ];

  function updateItems(newVariables = {}) {
    setPage(0);
    refetchItems({ ...variables, ...newVariables });
  }

  const onPageChange = ({ direction }) => {
    const hasNewPage = itemsData[`has${_capitalize(direction)}`];

    if (!hasNewPage) return;

    const next = direction === 'next' ? itemsData.next : '';
    const previous = direction === 'previous' ? itemsData.previous : '';
    const newPageVariables = { ...variables, next, previous };
    refetchItems(newPageVariables);
  };

  const getRowLink = ({ id }) => `items/${id}`;

  const onChangeTerm = (e) => setTerm(e?.target?.value);

  function renderPublished({ published: isPublished }) {
    const classProp = isPublished ? 'published' : 'unpublished';
    return (
      <Typography className={classes[classProp]}>
        {isPublished ? 'Published' : 'Unpublished'}
      </Typography>
    );
  }

  function renderDelete({ id }) {
    return (
      <IconButton size="small" onClick={() => onItemDelete(id)}>
        <DeleteIcon />
      </IconButton>
    );
  }

  function onItemDelete(id) {
    deleteCollectionTypePropertyItem({ variables: { id } });
  }

  const filterFields = [
    {
      type: 'text',
      placeholder: 'Filter by name',
      id: 'term',
      value: term,
      onChange: onChangeTerm,
    },
    {
      value: property,
      onChange: (e, value) => setProperty(value || {}),
      id: 'property_type',
      placeholder: 'Filter by property type',
      options: propertiesOptions,
    },
  ];

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Typography className={classes.title} variant="h5">
        All Unpublished Properties
      </Typography>
      <div className={classes.filters}>
        <DashboardFilters fields={filterFields} />
      </div>
      <div className={classes.table}>
        <DashboardTable
          data={items}
          loading={loading}
          columns={COLUMNS}
          getRowLink={getRowLink}
          LinkComponent={PropertyDashboardLink}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          count={itemsData?.resultsNumber}
          onPageChange={onPageChange}
        />
      </div>
    </Container>
  );
};

export default PropertyDashboard;
